export const githubConfig = {
  name: "github",
  displayName: "GitHub",
  description: "Source code repository security scanning",
  icon: "/git.svg",
  category: "Source Control",

  // API Endpoints
  endpoints: {
    list: {
      url: "api/repository/unified",
      method: "POST",
      useFilters: true,
      filterPreset: "onboardedRepositories",
    },
    details: "api/repository/{id}",
    findings: {
      url: "api/findings/repo/{id}",
      method: "POST",
      useFilters: true,
    },
    stats: "api/findings/cards",
    create: "api/repository",
    update: "api/repository/{id}",
    delete: "api/repository/{id}",
    nonOnboarded: "api/repository/non-onboarded",
    updateStates: "api/repository/{id}/update-states",
    searchOnboarded: {url:"api/github/search", method:"GET"},
    searchGithub: "api/github/search-github",
    branches: "api/github/branches",
    searchBranch: "api/github/search-branch",
  },

  // Card Configuration
  cardFields: [
    {
      key: "name",
      label: "Repository Name",
      type: "title",
      truncate: true,
    },
    {
      key: "owner",
      label: "Owner",
      type: "subtitle",
    },
    {
      key: "states.isScanningCount",
      label: "Scan Status",
      type: "status",
      statusMap: {
        0: { color: "gray", label: "Scans Not Scheduled" },
        1: { color: "orange", label: "1 scan running" },
        default: { color: "orange", label: "{value} scans running" },
      },
    },
    {
      key: "states.totalCount",
      label: "Branches Onboarded",
      type: "number",
    },
  ],

  // Table Columns for Findings
  findingColumns: [
    {
      accessorKey: "verified",
      header: "Category",
      cell: "verification_status",
    },
    {
      accessorKey: "branch",
      header: "Branch",
      cell: "truncated_text_with_tooltip",
      maxLength: 20,
    },
    {
      accessorKey: "email",
      header: "Developer Email",
      cell: "truncated_email_with_tooltip",
      maxLength: 20,
    },
    {
      accessorKey: "detector_type",
      header: "Source",
      cell: "text",
    },
    {
      accessorKey: "github_link",
      header: "File",
      cell: "file_link",
    },
    {
      accessorKey: "raw_result",
      header: "Raw Result",
      cell: "truncated_text",
      maxLength: 10,
    },
    {
      accessorKey: "insights",
      header: "Insights",
      cell: "repo_insights",
    },
  ],

  // Stats Configuration
  statsCards: [
    {
      key: "total_repos",
      title: "Total Repositories",
      dataPath: "total_repos.total",
      subStats: [
        {
          key: "onboarded",
          label: "Onboarded",
          dataPath: "total_repos.onboarded",
        },
        {
          key: "not_onboarded",
          label: "Not onboarded",
          dataPath: "total_repos.not_onboarded",
        },
      ],
    },
    {
      key: "vulnerable_sources",
      title: "Vulnerable Sources",
      dataPath: "vulnerable_sources.sources",
      type: "tag_list",
      displayKey: "detector_type",
      countKey: "count",
      // Limit how many items should be shown in the UI for this card
      maxItems: 10,
    },
    {
      key: "scan_completion",
      title: "Scan Completion",
      dataPath: "scan_status.completed",
      subText: "Completed in last {hours}h",
      subTextPath: "scan_status.hours_period",
      subStats: [
        {
          key: "running",
          label: "Running",
          dataPath: "scan_status.running_scans",
        },
        {
          key: "upcoming",
          label: "Upcoming",
          dataPath: "scan_status.upcoming_scans",
        },
      ],
    },
    {
      key: "critical_issues",
      title: "Critical Issues",
      dataPath: "critical_issues.active_tokens",
      badge: { text: "Active", color: "red" },
      subText: "of {total} Total Findings",
      subTextPath: "critical_issues.total_tokens",
      subStats: [
        {
          key: "unvalidated",
          label: "Unvalidated",
          dataPath: "critical_issues.unvalidated_tokens",
        },
        {
          key: "ignored_active",
          label: "Ignored Active",
          dataPath: "critical_issues.ignored_active_tokens",
        },
        {
          key: "ignored_unvalidated",
          label: "Ignored Unvalidated",
          dataPath: "critical_issues.ignored_unvalidated_tokens",
        },
      ],
    },
  ],

  // Action Menu Items
  actions: [
    {
      key: "create_ticket",
      label: "Create Ticket",
      icon: "ExternalLink",
      action: "api",
      endpoint: "api/jira/tickets/bulk",
      method: "POST",
      baseUrl: "REACT_APP_URL",
      condition: "normal", // Show when !hasJiraTicket && !isResolved && !isIgnored
    },
    {
      key: "ignore_findings",
      label: "Ignore Finding",
      icon: "X",
      action: "api",
      endpoint: "api/findings/bulk/status",
      method: "PUT",
      baseUrl: "REACT_APP_URL",
      payload: { status: "ignore" },
      condition: "normal", // Show when !hasJiraTicket && !isResolved && !isIgnored
    },
    {
      key: "resolve_ticket",
      label: "Resolve Ticket",
      icon: "X",
      action: "api",
      endpoint: "api/jira/tickets/bulk/ignore",
      method: "PUT",
      baseUrl: "REACT_APP_URL",
      condition: "hasTicket", // Show when hasJiraTicket && !isResolved && !isIgnored
    },
    {
      key: "unignore_findings",
      label: "Unignore Finding",
      icon: "ExternalLink",
      action: "api",
      endpoint: "api/findings/bulk/status",
      method: "PUT",
      baseUrl: "REACT_APP_URL",
      payload: { status: "not_ignore" },
      condition: "ignored", // Show when isIgnored
    },
    {
      key: "auto_fix",
      label: "Auto Fix",
      icon: "Tool",
      action: "api",
      endpoint: "api/v1/remediation/auto-fix",
      method: "POST",
      baseUrl: "AI_URL",
      condition: "all", // Show in all states
    },
    {
      key: "move_to_vault",
      label: "Move to Vault",
      icon: "Archive",
      action: "api",
      endpoint: "api/v1/remediation/vault-ingest",
      method: "POST",
      baseUrl: "AI_URL",
      payload: { scan_type: "github" },
      condition: "all", // Show in all states
    },
  ],

  // Bulk Actions Configuration
  bulkActions: [
    {
      key: "createTickets",
      label: "Create Tickets",
      action: "createTickets",
      requiresValidation: "ticketing",
      endpoint: "api/jira/tickets/bulk",
      method: "POST",
      baseUrl: "REACT_APP_URL",
    },
    {
      key: "ignoreFindings",
      label: "Ignore Findings",
      action: "ignoreFindings",
      endpoint: "api/findings/bulk/status",
      method: "PUT",
      baseUrl: "REACT_APP_URL",
      payload: { status: "ignore" },
    },
    {
      key: "unignoreFindings",
      label: "Unignore Findings",
      action: "unignoreFindings",
      endpoint: "api/findings/bulk/status",
      method: "PUT",
      baseUrl: "REACT_APP_URL",
      payload: { status: "not_ignore" },
    },
    {
      key: "ignoreVulnerabilities",
      label: "Resolve Tickets",
      action: "ignoreVulnerabilities",
      requiresValidation: "ticketing",
      endpoint: "api/jira/tickets/bulk/ignore",
      method: "PUT",
      baseUrl: "REACT_APP_URL",
    },
    {
      key: "autoFix",
      label: "Auto Fix",
      action: "autoFix",
      requiresValidation: "vault",
      endpoint: "api/v1/remediation/auto-fix",
      method: "POST",
      baseUrl: "AI_URL",
    },
    {
      key: "moveToVault",
      label: "Move to Vault",
      action: "moveToVault",
      requiresValidation: "vault",
      endpoint: "api/v1/remediation/vault-ingest",
      method: "POST",
      baseUrl: "AI_URL",
      payload: { scan_type: "github" },
      payloadStructure: "vault", // Special structure: {items: [{scan_type, finding_ids}]}
    },
  ],

  filterConfig: {
    serverSideFiltering: true,
    serverSideSourceFiltering: true, // Backend handles source filtering via API payload
    supportedFilters: ["verification", "source", "status"],
    defaultFilter: "all",
    filterEndpoint: "api/findings/repo/{id}/filter-options",
    statusMapping: {
      verified: "active",
      unverified: "unvalidated",
      ignored: "ignored"
    },
    filterLabels: {
      verified: "Verified",
      unverified: "Unverified", 
      ignored: "Ignored"
    }
  },

  // Validation Requirements
  validation: {
    ticketing: {
      required: ["jira_url", "freshservice_url"],
      message:
        "Please enter the ticketing credentials in the configuration page",
    },
    vault: {
      required: ["hashicorp_vault_url"],
      message: "Please enter the vault credentials in the configuration page",
    },
  },

  // Integration-specific features
  features: {
    hasInsights: true,
    hasBlastRadius: true,
    supportsAutoFix: true,
    supportsVaultRemediation: true,
    supportsTicketing: true,
  },

  // Onboarding Configuration
  onboarding: {
    enabled: true,
    route: "/integration/github/onboarding",
    buttonText: "Scan New Repositories",
  },

  // Auto-fix polling configuration
  autoFixPolling: {
    enabled: true,
    statusEndpoint: "api/v1/remediation/jobs/{job_id}",
    pollInterval: 5000, // Poll every 5 seconds
    maxPollTime: 600000, // Stop polling after 10 minutes
    baseUrl: "AI_URL", // Use AI_URL for auto-fix endpoints
    enableLogging: false, // Set to true for debugging
  },
};